/*
 * The import wizard's session panel (audit Phase 3.3). Replaces
 * wwwroot/js/Import/Session.js on the Phase 3.1 core. No jQuery.
 *
 * A session is a staged spreadsheet: every row carries an outcome and a list
 * of issues, and nothing reaches the database until Commit. Per-row edits and
 * session actions all reload the whole panel, because a row's outcome and its
 * issue badges are recomputed server-side and a patched row would state them
 * wrongly.
 *
 * Paging and filtering are client-side: the server already ships every row in
 * the panel, so a page flip is a class toggle rather than a round trip. The
 * state is kept in sessionStorage under the session's own id, so the reload
 * that follows an edit does not drop the operator back to page 1.
 */
import { ajaxJson, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { all, one } from '../core/dom';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';

function sessionId(): string {
    return one('#ImportSessionPanel')?.getAttribute('data-session-id') ?? '';
}

function reloadPanel(): void {
    const id = sessionId();
    if (!id) return;
    void ajaxSwap({
        container: 'ViewContainer',
        url: `Import/Session/${id}`,
        alertTitle: 'Failed to reload import session',
    });
}

function row(rowNumber: number | string): Element | null {
    return one(`#ImportRow-${rowNumber}`);
}

function cells(rowElement: Element): HTMLInputElement[] {
    return Array.from(rowElement.querySelectorAll<HTMLInputElement>('input.import-cell'));
}

function toggleRowControls(rowElement: Element, editing: boolean): void {
    for (const node of Array.from(rowElement.querySelectorAll('.import-edit-btn'))) node.classList.toggle('d-none', editing);
    for (const node of Array.from(rowElement.querySelectorAll('.import-save-btn'))) node.classList.toggle('d-none', !editing);
    for (const node of Array.from(rowElement.querySelectorAll('.import-cancel-btn'))) node.classList.toggle('d-none', !editing);
}

/** POST a per-row verb and redraw; every one of them changes the row's outcome. */
function rowAction(rowNumber: number | string, suffix: string, successTitle: string, failureTitle: string): void {
    void ajaxJson({
        url: `Import/Session/${sessionId()}/Rows/${rowNumber}/${suffix}`,
        method: 'POST',
        errorTitle: failureTitle,
        onSuccess: () => {
            toast('success', successTitle);
            reloadPanel();
        },
        onFailure: failToast(failureTitle),
    });
}

export const Session = {
    BeginEditRow(rowNumber: number | string): void {
        const rowElement = row(rowNumber);
        if (!rowElement) return;
        const inputs = cells(rowElement);
        for (const input of inputs) input.disabled = false;
        toggleRowControls(rowElement, true);
        inputs[0]?.focus();
    },

    CancelEditRow(rowNumber: number | string): void {
        const rowElement = row(rowNumber);
        if (!rowElement) return;
        for (const input of cells(rowElement)) {
            input.value = input.dataset['original'] ?? '';
            input.disabled = true;
        }
        toggleRowControls(rowElement, false);
    },

    SaveRow(rowNumber: number | string): void {
        const rowElement = row(rowNumber);
        if (!rowElement) return;
        const payload: Record<string, string> = {};
        for (const cell of Array.from(rowElement.querySelectorAll<HTMLElement>('td[data-header]'))) {
            const header = cell.dataset['header'];
            const input = cell.querySelector<HTMLInputElement>('input.import-cell');
            if (header && input) payload[header] = input.value;
        }
        void ajaxJson({
            url: `Import/Session/${sessionId()}/Rows/${rowNumber}/Edit`,
            method: 'POST',
            // [FromBody] EditRowModel: a JSON body, not a form post.
            contentType: 'application/json',
            data: { cells: payload },
            errorTitle: 'Failed to save row',
            onSuccess: () => {
                toast('success', 'Row updated', 'Re-validate to refresh issues.');
                reloadPanel();
            },
            onFailure: failToast('Failed to save row'),
        });
    },

    RemoveRow(rowNumber: number | string): void {
        void confirm({
            title: 'Remove row',
            html: `Remove row ${rowNumber} from this import session?<br/><br/>`
                + 'The row stays in the session history but is excluded from the commit.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => rowAction(rowNumber, 'Remove', 'Row removed', 'Failed to remove row'),
        });
    },

    RestoreRow(rowNumber: number | string): void {
        rowAction(rowNumber, 'Restore', 'Row restored', 'Failed to restore row');
    },

    OverrideRow(rowNumber: number | string): void {
        void confirm({
            title: 'Override row errors',
            html: `Commit row ${rowNumber} despite its errors?<br/><br/>`
                + 'The engine still applies the row, so whatever the errors describe is what gets written.',
            confirmText: 'Override',
            variant: 'warning',
            onConfirm: () => rowAction(rowNumber, 'Override', 'Row overridden', 'Failed to override row'),
        });
    },

    Revalidate(): void {
        void ajaxJson({
            url: `Import/Session/${sessionId()}/Revalidate`,
            method: 'POST',
            errorTitle: 'Re-validate failed',
            onSuccess: () => {
                toast('success', 'Re-validated');
                reloadPanel();
            },
            onFailure: failToast('Re-validate failed'),
        });
    },

    Commit(): void {
        void confirm({
            title: 'Commit import',
            html: 'Apply this import to the database?<br/><br/>'
                + 'Audit and cross-domain integrity rules run automatically. '
                + '<span class="text-warning">There is no undo: reversing a commit means separate edits or '
                + 'a follow-up import.</span>',
            confirmText: 'Commit',
            variant: 'primary',
            onConfirm: () => {
                void ajaxJson({
                    url: `Import/Session/${sessionId()}/Commit`,
                    method: 'POST',
                    errorTitle: 'Commit failed',
                    onSuccess: data => {
                        // A 200 that says `committed: false` is the engine
                        // REFUSING, not a transport failure -- it carries its
                        // own reason and must not read as success.
                        if (data['committed']) {
                            toast('success', 'Commit succeeded',
                                `Inserted ${data['inserted'] ?? 0}, updated ${data['updated'] ?? 0}, `
                                + `deleted ${data['deleted'] ?? 0}, skipped ${data['skipped'] ?? 0}.`);
                        } else {
                            toast('warning', 'Commit refused',
                                String(data['failureMessage'] ?? 'The engine refused the commit.'));
                        }
                        reloadPanel();
                    },
                    onFailure: data => {
                        toast('danger', 'Commit failed', summaryOf(data));
                        return true;
                    },
                });
            },
        });
    },

    Cancel(): void {
        void confirm({
            title: 'Cancel session',
            message: 'Cancel this import session? Nothing is applied to the database, and the staged rows '
                + 'are no longer available to commit.',
            confirmText: 'Cancel session',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Import/Session/${sessionId()}/Cancel`,
                    method: 'POST',
                    errorTitle: 'Failed to cancel session',
                    onSuccess: () => {
                        toast('success', 'Session cancelled');
                        reloadPanel();
                    },
                    onFailure: failToast('Failed to cancel session'),
                });
            },
        });
    },

    Paging: {} as PagingApi,      // assigned below; the views name Import.Session.Paging.*
};

// ------------------------------------------------- client-side paging + filter

interface PagingState { pageSize: number; currentPage: number; filter: string }

const STORAGE_PREFIX = 'mcp.import.paging.';
const DEFAULTS: PagingState = { pageSize: 25, currentPage: 1, filter: 'all' };
let state: PagingState = { ...DEFAULTS };

function loadState(id: string): PagingState {
    if (!id) return { ...DEFAULTS };
    try {
        const raw = window.sessionStorage.getItem(STORAGE_PREFIX + id);
        if (!raw) return { ...DEFAULTS };
        const parsed = JSON.parse(raw) as Partial<PagingState>;
        return {
            pageSize: Number.parseInt(String(parsed.pageSize), 10) || DEFAULTS.pageSize,
            currentPage: Number.parseInt(String(parsed.currentPage), 10) || DEFAULTS.currentPage,
            filter: parsed.filter || DEFAULTS.filter,
        };
    } catch {
        return { ...DEFAULTS };
    }
}

function saveState(id: string): void {
    if (!id) return;
    try {
        window.sessionStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(state));
    } catch {
        /* quota or private browsing: the paging state is a convenience, not data */
    }
}

function filteredRows(): Element[] {
    const rows = all('#ImportRowsTable tbody tr[data-row-number]');
    return state.filter === 'all' ? rows : rows.filter(r => r.getAttribute('data-outcome') === state.filter);
}

function totalPages(): number {
    return Math.max(1, Math.ceil(filteredRows().length / state.pageSize));
}

function setDisabled(selector: string, disabled: boolean): void {
    const node = one(selector) as HTMLButtonElement | null;
    if (node) node.disabled = disabled;
}

/** Show the current page's rows and nothing else, then restate the toolbar. */
function apply(): void {
    // The rows tab may not be in the DOM -- the operator can be sitting on
    // Summary when the panel reloads.
    const table = one('#ImportRowsTable');
    if (!table) return;

    const pages = totalPages();
    state.currentPage = Math.min(Math.max(state.currentPage, 1), pages);

    for (const node of Array.from(table.querySelectorAll('tbody tr[data-row-number]'))) node.classList.add('d-none');
    for (const node of Array.from(table.querySelectorAll('tbody tr.import-live-row'))) node.classList.add('d-none');

    const showDiff = (one('#ImportShowDiffToggle') as HTMLInputElement | null)?.checked ?? false;
    const rows = filteredRows();
    const startIndex = (state.currentPage - 1) * state.pageSize;
    const endIndex = startIndex + state.pageSize;
    for (const node of rows.slice(startIndex, endIndex)) {
        node.classList.remove('d-none');
        if (!showDiff) continue;
        const rowNumber = node.getAttribute('data-row-number') ?? '';
        // A live-diff row belongs to its data row, so it follows both the page
        // and the filter -- otherwise flipping pages resurrects hidden ones.
        for (const live of Array.from(table.querySelectorAll(`tr.import-live-row[data-for-row="${CSS.escape(rowNumber)}"]`))) {
            live.classList.remove('d-none');
        }
    }

    const total = rows.length;
    const first = total === 0 ? 0 : startIndex + 1;
    const last = Math.min(endIndex, total);
    const info = one('#ImportRowsPageInfo');
    if (info) {
        info.textContent = total === 0
            ? 'No rows match the current filter.'
            : `${first}–${last} of ${total}${state.filter === 'all' ? ' rows' : ' (filtered) rows'}`;
    }
    const pageOf = one('#ImportRowsPageOf');
    if (pageOf) pageOf.textContent = `of ${pages}`;
    const current = one('#ImportRowsCurrentPage') as HTMLInputElement | null;
    if (current) {
        current.value = String(state.currentPage);
        current.max = String(pages);
    }
    setDisabled('#ImportRowsPageFirst', state.currentPage <= 1);
    setDisabled('#ImportRowsPagePrev', state.currentPage <= 1);
    setDisabled('#ImportRowsPageNext', state.currentPage >= pages);
    setDisabled('#ImportRowsPageLast', state.currentPage >= pages);

    saveState(sessionId());
}

interface PagingApi {
    Init(): void;
    SetPageSize(this: Element): void;
    SetFilter(this: Element): void;
    ToggleDiff(): void;
    First(): void;
    Prev(): void;
    Next(): void;
    Last(): void;
    GoToPage(value: string | number): void;
}

export const Paging: PagingApi = {
    /**
     * Start from the persisted state and push it back into the controls, which
     * the server renders with its own defaults.
     */
    Init(): void {
        state = loadState(sessionId());
        const pageSize = one('#ImportRowsPageSize') as HTMLSelectElement | null;
        if (pageSize) pageSize.value = String(state.pageSize);
        const filter = one('#ImportRowsFilter') as HTMLSelectElement | null;
        if (filter) {
            // The filter's options depend on the outcomes present in THIS
            // render, so a stored filter may no longer exist -- the operator
            // may have removed every Error row since.
            if (filter.querySelector(`option[value="${CSS.escape(state.filter)}"]`)) {
                filter.value = state.filter;
            } else {
                state.filter = 'all';
                filter.value = 'all';
            }
        }
        apply();
    },

    SetPageSize(this: Element): void {
        state.pageSize = Number.parseInt((this as HTMLSelectElement).value, 10) || DEFAULTS.pageSize;
        state.currentPage = 1;
        apply();
    },

    SetFilter(this: Element): void {
        state.filter = (this as HTMLSelectElement).value || 'all';
        state.currentPage = 1;
        apply();
    },

    /** The show-diff checkbox: re-running the layout is what honours it. */
    ToggleDiff(): void {
        apply();
    },

    First(): void { state.currentPage = 1; apply(); },
    Prev(): void { state.currentPage -= 1; apply(); },
    Next(): void { state.currentPage += 1; apply(); },
    Last(): void { state.currentPage = totalPages(); apply(); },

    GoToPage(value: string | number): void {
        const page = Number.parseInt(String(value), 10);
        state.currentPage = Number.isFinite(page) && page >= 1 ? page : 1;
        apply();
    },
};

Session.Paging = Paging;

/**
 * Lay the rows out after every render. This used to be an inline `<script>` at
 * the foot of _RowsTab.cshtml, which worked only because swapHtml re-executes
 * inserted scripts; an enhancer covers the tab-load path as well and keeps the
 * partial free of script.
 */
export function enhanceImportRows(root: ParentNode): void {
    const table = root.querySelector('#ImportRowsTable') ?? (root instanceof Element && root.matches('#ImportRowsTable') ? root : null);
    if (!table) return;
    Paging.Init();
}

let bound = false;

/**
 * The page-number box. core/dispatch fires a data-action on CHANGE only for a
 * select, a textarea, a toggle input or a file picker -- a number input would
 * fire on click, which is every spinner press and no typed value at all -- so
 * this one control needs a listener of its own.
 */
export function initImportArea(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('change', event => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.id !== 'ImportRowsCurrentPage') return;
        Paging.GoToPage(target.value);
    });
}
